<template>
  <v-dialog :value="value" max-width="1000" scrollable persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        {{ formTitle }}
      </v-card-title>

      <v-card-text>
        <ValidationObserver ref="formObserver">
          <v-form :disabled="isLoading">
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <vee-text-field v-model="item.name" rules="required|max:128" label="Name*" />
                </v-col>

                <v-col cols="12" sm="6">
                  <vee-select
                    v-model="item.type"
                    :rules="'required|oneOf:' + Object.values(FlowExercisesType).join(',')"
                    :items="flowTypeOptions"
                    label="Type*"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-checkbox v-model="item.active" label="Active" />
                </v-col>

                <v-col cols="12">
                  <vee-autocomplete
                    v-model="item.exercises"
                    :loading="areExercisesLoading"
                    :items="filteredExercises"
                    label="Exercises*"
                    item-text="name"
                    item-value="uniqueKey"
                    return-object
                    multiple
                    clearable
                    rules="required|arrayRequired"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        close
                        small
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        @click="data.select"
                        @click:close="removeExercise(data.item)"
                      >
                        {{ data.item.name }}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ data.item.id }} /
                          {{ data.item.uniqueKey }} /
                          <v-chip small>
                            {{ data.item.category }}
                          </v-chip>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </vee-autocomplete>
                </v-col>

                <v-col cols="12">
                  <vee-rich-text-editor
                    v-model="item.summary"
                    label="Summary"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </ValidationObserver>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="onCancel">
          Cancel
        </v-btn>
        <v-btn text :loading="isLoading" @click="onSave">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  computed, onMounted, ref, nextTick,
} from 'vue'
import { ValidationObserver } from 'vee-validate'
import useExercises from '@/composables/api/exercises'
import { ExerciseCategory, FlowExercisesType } from '@/enums/enums'

export default {
  components: {
    ValidationObserver,
  },

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      required: true,
      type: Object,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { exercises, isLoading: areExercisesLoading, fetchExercises } = useExercises()
    const formObserver = ref(null)

    const filteredExercises = computed(() => exercises.value.filter(exercise => exercise.category === ExerciseCategory.FLOW))

    const flowTypeOptions = computed(() => [
      { text: 'Quiet Mind', value: FlowExercisesType.QUIET_MIND },
      { text: 'Breathing', value: FlowExercisesType.BREATHING },
      { text: 'Connection', value: FlowExercisesType.CONNECTION },
      { text: 'Preparation', value: FlowExercisesType.PREPARATION },
      { text: 'Commitment', value: FlowExercisesType.COMMITMENT },
      { text: 'Pressure', value: FlowExercisesType.PRESSURE },
    ])

    const formTitle = computed(() => (
      props.item.id === '' ? 'Create a Flow session' : 'Edit the Flow session'
    ))

    onMounted(async () => {
      await fetchExercises(ExerciseCategory.FLOW)
      if (props.item.active === undefined) {
        props.item.active = true
      }
      props.item.category = ExerciseCategory.FLOW
      if (!props.item.level) {
        props.item.level = 1
      }
    })

    const onCancel = () => {
      emit('cancel')
    }

    const onSave = async () => {
      await nextTick()
      const isValid = await formObserver.value.validate()

      if (isValid) {
        const session = structuredClone(props.item)
        session.category = ExerciseCategory.FLOW
        session.exercises = session.exercises.map(({ uniqueKey }) => ({ uniqueKey }))
        session.level = 1

        emit('save', session)
      }
    }

    const removeExercise = item => {
      const index = props.item.exercises.findIndex(value => item.uniqueKey === value.uniqueKey)

      if (index >= 0) {
        props.item.exercises.splice(index, 1)
      }
    }

    return {
      formObserver,
      formTitle,
      areExercisesLoading,
      filteredExercises,
      flowTypeOptions,
      onCancel,
      onSave,
      removeExercise,
      FlowExercisesType,
    }
  },
}
</script>
